/**
 * CoachPlaybook - ActionWheel
 * Version: 0.6.0
 * Description: Radial context menu for sport-specific player actions on the playing surface
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';

// ---------------------------------------------------------------------------
// SVG Icon Paths — clean geometric icons for each action category/type
// ---------------------------------------------------------------------------
const ACTION_ICONS = {
  // Movement
  move: (
    <path d="M12 4l6 8-6 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  run: (
    <path d="M12 4l6 8-6 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  sprint: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4l6 8-6 8" />
      <path d="M6 4l6 8-6 8" />
    </g>
  ),
  cut: (
    <path d="M6 18L18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  flash: (
    <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  curl: (
    <path d="M6 18Q6 6 18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  ),
  fade: (
    <path d="M6 6l12 12M14 18h4v-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  pop: (
    <path d="M12 18V6M8 10l4-4 4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  roll: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6a6 6 0 1 1 0 12" />
      <path d="M12 18l3-3-3-3" />
    </g>
  ),
  replace: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 1l4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <path d="M7 23l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </g>
  ),
  overlap: (
    <path d="M6 18C6 12 12 12 18 6M14 6h4v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'diagonal-run': (
    <path d="M6 18L18 6M14 6h4v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'check-run': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V8" />
      <path d="M8 12l4-4 4 4" />
      <path d="M8 18Q12 14 16 18" />
    </g>
  ),
  'drop-deep': (
    <path d="M12 6v12M8 14l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'recovery-run': (
    <path d="M18 6L6 18M6 18V12M6 18h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),

  // Ball actions
  pass: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
      <path d="M8 12h8M14 9l3 3-3 3" />
    </g>
  ),
  'through-ball': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20V4M8 8l4-4 4 4" />
      <path d="M8 16h8" strokeDasharray="2 2" />
    </g>
  ),
  cross: (
    <path d="M4 18Q12 6 20 10M16 8l4 2-2 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'bounce-pass': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="8" r="2" />
      <circle cx="18" cy="8" r="2" />
      <path d="M8 9l4 7 4-7" />
    </g>
  ),
  'lob-pass': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="16" r="2" />
      <circle cx="18" cy="16" r="2" />
      <path d="M8 15Q12 4 16 15" />
    </g>
  ),
  'skip-pass': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="4" cy="16" r="2" />
      <circle cx="20" cy="8" r="2" />
      <path d="M6 15L18 9M15 7l3 2-1 3" />
    </g>
  ),
  'entry-pass': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12M8 12l4 4 4-4" />
      <rect x="8" y="18" width="8" height="2" rx="1" />
    </g>
  ),
  'one-two': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="12" r="2" />
      <circle cx="18" cy="12" r="2" />
      <path d="M8 11l8-2" />
      <path d="M16 13l-8 2" />
    </g>
  ),
  'back-pass': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 12H6M10 8l-4 4 4 4" />
    </g>
  ),
  dribble: (
    <path d="M6 12Q9 6 12 12Q15 18 18 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  ),
  'skill-move': (
    <path d="M6 18Q8 12 12 12Q16 12 18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  ),
  handoff: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 9l4 3-4 3" />
      <path d="M19 9l-4 3 4 3" />
      <path d="M9 12h6" />
    </g>
  ),
  crossover: (
    <path d="M6 6l12 12M6 18L18 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  ),
  'spin-move': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6a6 6 0 1 0 0 12" />
      <path d="M12 18v-4l3 2" />
    </g>
  ),
  'hold-up': (
    <path d="M12 18V8M8 8h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),

  // Screen actions
  screen: (
    <rect x="8" y="6" width="8" height="12" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
  ),
  'back-screen': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="6" width="8" height="12" rx="1" />
      <path d="M12 20l-3-3h6z" />
    </g>
  ),
  'ball-screen': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="8" width="8" height="10" rx="1" />
      <circle cx="12" cy="5" r="2" />
    </g>
  ),
  'down-screen': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="4" width="8" height="10" rx="1" />
      <path d="M12 16v4M9 18l3 2 3-2" />
    </g>
  ),
  'flare-screen': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="6" width="8" height="12" rx="1" />
      <path d="M18 6l3-3M18 18l3 3" />
    </g>
  ),
  'cross-screen': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="6" width="6" height="12" rx="1" />
      <path d="M4 12h3M17 12h3" />
    </g>
  ),
  slip: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="6" width="8" height="8" rx="1" />
      <path d="M12 14v6" strokeDasharray="2 2" />
    </g>
  ),

  // Scoring
  shoot: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4v3M12 17v3M4 12h3M17 12h3" />
    </g>
  ),
  layup: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 18Q8 10 14 6" />
      <circle cx="16" cy="5" r="2" />
    </g>
  ),
  dunk: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v10" />
      <path d="M8 18h8" />
      <circle cx="12" cy="16" r="2" />
    </g>
  ),
  floater: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 18Q12 2 16 18" strokeDasharray="3 2" />
      <circle cx="12" cy="6" r="2" />
    </g>
  ),
  header: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M12 4v-2M8 18l4-6 4 6" />
    </g>
  ),
  volley: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="10" r="2" />
      <path d="M10 10l8 4M15 12l3 2-1 3" />
    </g>
  ),
  'alley-oop': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 18Q12 0 20 12" />
      <circle cx="20" cy="14" r="2" />
    </g>
  ),

  // Post play
  'post-up': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="8" width="8" height="10" rx="2" />
      <path d="M12 6v2" />
    </g>
  ),
  seal: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8h8v8H8z" />
      <path d="M12 4v4M12 16v4" />
    </g>
  ),

  // Footwork
  jab: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6v8" />
      <path d="M8 18l4-4 4 4" />
    </g>
  ),
  pivot: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="2" />
      <path d="M12 12a6 6 0 0 1 5-3" />
      <path d="M12 12a6 6 0 0 0-5-3" />
    </g>
  ),
  'euro-step': (
    <path d="M6 18l4-6-2-4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'step-back': (
    <path d="M18 12H6M10 8l-4 4 4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),

  // Defense
  deny: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="8" />
      <path d="M6 18L18 6" />
    </g>
  ),
  closeout: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6" />
      <path d="M18 6v5M18 6h-5" />
      <path d="M5 17l2 2" strokeDasharray="1 2" />
    </g>
  ),
  contest: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v8" />
      <path d="M8 6l4-2 4 2" />
      <path d="M6 20h12" />
    </g>
  ),
  switch: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8h12M14 5l3 3-3 3" />
      <path d="M18 16H6M10 13l-3 3 3 3" />
    </g>
  ),
  hedge: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h16" />
      <path d="M12 4v8" />
      <path d="M8 8l4-4 4 4" />
    </g>
  ),
  ice: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v16M8 8l4-4 4 4" />
      <path d="M6 12h12" />
      <path d="M8 16h8" />
    </g>
  ),
  drop: (
    <path d="M12 4v16M8 16l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  blitz: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l6 6M18 6l-6 6" />
      <path d="M12 12v6" />
    </g>
  ),
  help: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12Q12 4 18 12" />
      <path d="M12 8v6M12 17v1" />
    </g>
  ),
  rotate: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6a6 6 0 1 1-5 3" />
      <path d="M7 9L7 5h4" />
    </g>
  ),
  recover: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 12H6" />
      <path d="M10 8l-4 4 4 4" />
      <path d="M18 8v8" />
    </g>
  ),
  trap: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l6 12 6-12" />
      <path d="M6 6h12" />
    </g>
  ),
  mark: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M6 20c0-4 3-6 6-6s6 2 6 6" />
    </g>
  ),
  'track-run': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="2" />
      <path d="M10 9l8 8" strokeDasharray="3 2" />
    </g>
  ),
  tackle: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18l12-12" />
      <path d="M14 6h4v4" />
      <circle cx="7" cy="17" r="2" />
    </g>
  ),
  intercept: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h16" />
      <path d="M12 4v16" />
      <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.3" />
    </g>
  ),
  press: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12M8 12l4 4 4-4" />
      <path d="M6 20h12" />
    </g>
  ),
  cover: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 5v6c0 4-3.5 7-8 9-4.5-2-8-5-8-9V8z" />
    </g>
  ),
  defend: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l8 5v6c0 4-3.5 7-8 9-4.5-2-8-5-8-9V8z" />
    </g>
  ),
  shift: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12h12M14 8l4 4-4 4" />
      <path d="M6 6v12" />
    </g>
  ),
  'offside-trap': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 14h16" />
      <path d="M8 10l4-4 4 4" />
      <circle cx="8" cy="14" r="1.5" fill="currentColor" />
      <circle cx="16" cy="14" r="1.5" fill="currentColor" />
    </g>
  ),
  clear: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V6M8 10l4-4 4 4" />
      <circle cx="12" cy="20" r="2" />
    </g>
  ),
  wall: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="8" rx="1" />
      <path d="M8 8v8M12 8v8M16 8v8" />
    </g>
  ),

  // Transition
  'fast-break': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h14M14 8l4 4-4 4" />
      <path d="M4 8l2 4-2 4" />
    </g>
  ),
  'counter-attack': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h14M14 8l4 4-4 4" />
      <path d="M4 8l2 4-2 4" />
    </g>
  ),
  outlet: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="16" r="2" />
      <path d="M8 15L18 7M15 5l3 2-1 4" />
    </g>
  ),
  'rim-run': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6M14 6h4v4" />
      <path d="M16 4h4v4" strokeWidth="1.5" />
    </g>
  ),
  trail: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12h12" strokeDasharray="3 2" />
      <path d="M14 8l4 4-4 4" />
    </g>
  ),
  'leak-out': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V6M8 10l4-4 4 4" strokeDasharray="3 2" />
    </g>
  ),
  'sprint-back': (
    <path d="M18 6L6 18M6 18V12M6 18h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'match-up': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="16" r="3" />
      <path d="M10 10l4 4" strokeDasharray="2 2" />
    </g>
  ),
  'build-up': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V6" />
      <path d="M8 10l4-4 4 4" />
      <path d="M6 14h12" strokeDasharray="2 2" />
    </g>
  ),
  possession: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="6" />
      <path d="M12 6a6 6 0 0 1 0 12" strokeDasharray="3 2" />
    </g>
  ),
  'quick-restart': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </g>
  ),

  // Goalkeeper
  'gk-save': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6v12h12V6" />
      <path d="M10 12h4M12 10v4" />
    </g>
  ),
  'gk-punch': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V10M8 14l4-4 4 4" />
      <circle cx="12" cy="7" r="2" />
    </g>
  ),
  'gk-distribute': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="14" width="12" height="6" rx="1" />
      <path d="M12 14V4M8 8l4-4 4 4" />
    </g>
  ),
  'gk-sweep': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6" />
      <circle cx="6" cy="18" r="2" />
      <path d="M14 6h4v4" />
    </g>
  ),

  // Rebounding
  rebound: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v6M12 14v6" />
      <path d="M8 8l4-4 4 4" />
      <rect x="6" y="10" width="12" height="4" rx="1" />
    </g>
  ),
  'box-out': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="1" />
      <path d="M12 10v4M10 12h4" />
    </g>
  ),
  'box-out-def': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="1" />
      <path d="M12 10v4M10 12h4" />
    </g>
  ),
  'crash-boards': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v6M12 14v6M8 8l4-4 4 4" />
      <rect x="6" y="10" width="12" height="4" rx="1" />
    </g>
  ),

  // Set pieces
  'free-kick': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="16" r="3" />
      <path d="M12 13V5M9 8l3-3 3 3" />
    </g>
  ),
  'corner-kick': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20V4h16" />
      <path d="M4 4Q12 4 12 12" />
    </g>
  ),
  'throw-in': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="6" r="3" />
      <path d="M12 9v8M8 13l4-2 4 2" />
    </g>
  ),
  'goal-kick': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="16" width="12" height="4" rx="1" />
      <path d="M12 16V6M8 10l4-4 4 4" />
    </g>
  ),
  penalty: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="16" r="2" />
      <rect x="4" y="4" width="16" height="8" rx="1" />
    </g>
  ),

  // Misc
  'pick-up': (
    <path d="M8 4v8M16 4v8M6 14h12v4H6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'behind-back': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 12Q12 20 18 12" />
      <circle cx="12" cy="8" r="3" />
    </g>
  ),
  hesitation: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8v8M16 8v8" />
    </g>
  ),
  'in-out': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6v12M16 6v12M8 12h8" />
    </g>
  ),
  nutmeg: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 6v12M16 6v12" />
      <circle cx="12" cy="12" r="2" fill="currentColor" fillOpacity="0.4" />
    </g>
  ),
  'step-over': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="3" />
      <path d="M8 8Q12 4 16 8" />
    </g>
  ),
  'press-trigger': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </g>
  ),
  stunt: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8l4 8 4-8" />
      <path d="M12 4v4" />
    </g>
  ),
  dig: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12M8 12l4 4 4-4" />
      <path d="M8 20h8" />
    </g>
  ),
  show: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6z" />
    </g>
  ),
  steal: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 6l-8 8M10 6h4v4" />
    </g>
  ),
  block: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12" />
      <path d="M6 8h12" />
      <path d="M8 4h8" />
    </g>
  ),
  deflect: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 18l8-8 6 2" />
      <path d="M16 10l2 2" />
    </g>
  ),
  charge: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="8" />
      <rect x="9" y="8" width="6" height="8" rx="1" fill="currentColor" fillOpacity="0.3" />
    </g>
  ),
  'zone-shift': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="8" rx="1" />
      <path d="M12 8v8" />
      <path d="M16 12h4" />
    </g>
  ),
  'slide-tackle': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8l12 8" />
      <path d="M14 16h4" />
      <circle cx="6" cy="8" r="2" />
    </g>
  ),
  'block-shot': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 4v12M6 8h12" />
      <path d="M8 4h8" />
    </g>
  ),
  squeeze: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h4M20 12h-4M8 8v8M16 8v8" />
      <path d="M4 12l4-3M4 12l4 3M20 12l-4-3M20 12l-4 3" />
    </g>
  ),
  'drop-back': (
    <path d="M12 4v16M8 16l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  jockey: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 8Q12 12 8 16" />
      <path d="M16 8Q12 12 16 16" />
    </g>
  ),
  tip: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 18V8M8 12l4-4 4 4" />
      <circle cx="12" cy="5" r="2" />
    </g>
  ),
  'lay-off': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 6v8M8 10l4 4 4-4" />
      <path d="M8 18h8" />
    </g>
  ),
  flick: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="3" />
      <path d="M12 11V5M10 7l2-2 2 2" />
    </g>
  ),
  'switch-play': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12h16M14 8l4 4-4 4" />
      <circle cx="4" cy="12" r="2" />
    </g>
  ),
  underlap: (
    <path d="M18 18C18 12 12 12 6 6M10 6H6v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  ),
  'blind-side': (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3" />
      <path d="M12 11v7" strokeDasharray="2 2" />
      <path d="M9 15l3 3 3-3" />
    </g>
  ),
  lob: (
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="16" r="2" />
      <circle cx="18" cy="16" r="2" />
      <path d="M8 15Q12 4 16 15" />
    </g>
  ),
};

// Fallback icon for unmapped actions
const FALLBACK_ICON = (
  <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
);

// ---------------------------------------------------------------------------
// ActionWheel Component
// ---------------------------------------------------------------------------
const ActionWheel = ({ x, y, actions, onSelect, onDismiss, accentColor, sportId }) => {
  const containerRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [visible, setVisible] = useState(false);

  // Trigger entrance animation on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Dismiss on Escape key
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  // Dismiss on click outside wheel
  const handleBackdropClick = useCallback((e) => {
    if (e.target === containerRef.current) {
      onDismiss();
    }
  }, [onDismiss]);

  const handleSelect = useCallback((action, index) => {
    setSelectedIndex(index);
    // Brief highlight before callback
    setTimeout(() => onSelect(action), 150);
  }, [onSelect]);

  // Limit displayed items to 8 max for clean radial layout
  const displayActions = actions.slice(0, 8);
  const count = displayActions.length;
  const RING_RADIUS = 60;
  const ITEM_SIZE = 36;
  const startAngle = -Math.PI / 2; // 12 o'clock

  // ---------------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------------
  const backdropStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 50,
    cursor: 'default',
  };

  const wheelContainerStyle = {
    position: 'absolute',
    left: `${x}%`,
    top: `${y}%`,
    transform: `translate(-50%, -50%) scale(${visible ? 1 : 0.5})`,
    opacity: visible ? 1 : 0,
    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.15s ease-out',
    pointerEvents: 'none',
    zIndex: 51,
  };

  const centerDotStyle = {
    position: 'absolute',
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: accentColor || '#22d3ee',
    border: '2px solid rgba(255,255,255,0.6)',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: `0 0 8px ${accentColor || '#22d3ee'}66`,
    zIndex: 2,
  };

  return (
    <div
      ref={containerRef}
      style={backdropStyle}
      onClick={handleBackdropClick}
    >
      <div style={wheelContainerStyle}>
        {/* Center player dot */}
        <div style={centerDotStyle} />

        {/* Subtle ring guide */}
        <svg
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
          width={RING_RADIUS * 2 + ITEM_SIZE + 20}
          height={RING_RADIUS * 2 + ITEM_SIZE + 20}
          viewBox={`0 0 ${RING_RADIUS * 2 + ITEM_SIZE + 20} ${RING_RADIUS * 2 + ITEM_SIZE + 20}`}
        >
          <circle
            cx={(RING_RADIUS * 2 + ITEM_SIZE + 20) / 2}
            cy={(RING_RADIUS * 2 + ITEM_SIZE + 20) / 2}
            r={RING_RADIUS}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        </svg>

        {/* Action items */}
        {displayActions.map((action, i) => {
          const angle = startAngle + (2 * Math.PI * i) / count;
          const ax = Math.cos(angle) * RING_RADIUS;
          const ay = Math.sin(angle) * RING_RADIUS;
          const isHovered = hoveredIndex === i;
          const isSelected = selectedIndex === i;

          const itemStyle = {
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: ITEM_SIZE,
            height: ITEM_SIZE,
            transform: `translate(calc(-50% + ${ax}px), calc(-50% + ${ay}px)) scale(${isHovered ? 1.1 : 1})`,
            transition: 'transform 0.12s ease-out, box-shadow 0.12s ease-out',
            cursor: 'pointer',
            pointerEvents: 'auto',
            zIndex: 3,
          };

          const circleStyle = {
            width: ITEM_SIZE,
            height: ITEM_SIZE,
            borderRadius: '50%',
            backgroundColor: isSelected
              ? (accentColor || '#22d3ee')
              : 'rgba(20, 20, 28, 0.9)',
            border: `2px solid ${isSelected
              ? '#ffffff'
              : isHovered
                ? (accentColor || '#22d3ee')
                : 'rgba(255,255,255,0.15)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isSelected
              ? `0 0 12px ${accentColor || '#22d3ee'}88, 0 0 24px ${accentColor || '#22d3ee'}44`
              : isHovered
                ? `0 0 8px ${accentColor || '#22d3ee'}44`
                : '0 2px 6px rgba(0,0,0,0.5)',
            transition: 'background-color 0.12s, border-color 0.12s, box-shadow 0.12s',
          };

          const iconStyle = {
            width: 18,
            height: 18,
            color: isSelected ? '#ffffff' : (isHovered ? '#ffffff' : 'rgba(255,255,255,0.8)'),
            flexShrink: 0,
          };

          const labelStyle = {
            position: 'absolute',
            top: ITEM_SIZE + 3,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 9,
            fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
            fontWeight: 500,
            color: isHovered || isSelected ? '#ffffff' : 'rgba(255,255,255,0.55)',
            whiteSpace: 'nowrap',
            textAlign: 'center',
            pointerEvents: 'none',
            letterSpacing: '0.01em',
            textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            transition: 'color 0.12s',
          };

          const icon = ACTION_ICONS[action.id] || FALLBACK_ICON;

          return (
            <div
              key={action.id}
              style={itemStyle}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(action, i);
              }}
            >
              <div style={circleStyle}>
                <svg
                  style={iconStyle}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {icon}
                </svg>
              </div>
              <div style={labelStyle}>{action.name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActionWheel;
